import React, { FC, useCallback, useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import type { OrganisationMember } from '@dreamai/types';
import api from '../../api';
import { ReactComponent as ShieldTickIcon } from '../../icons/shield-tick.svg';
import { ReactComponent as AlertTriangleIcon } from '../../icons/alert-triangle-outline.svg';
import HandbookConfirmModal from './HandbookConfirmModal';
import InformationBarrierModal, {
  BarrierFormValues,
  ScopeSummary,
  WeakRuleWarning,
} from './InformationBarrierModal';
import OrganisationToggle from './OrganisationToggle';
import { httpStatus } from './handbookTree';
import type {
  CheckInformationBarrierRequest,
  CheckInformationBarrierResponse,
  CreateInformationBarrierRequest,
  DeleteInformationBarrierResponse,
  InformationBarrier,
  InformationBarrierResponse,
  ListInformationBarriersResponse,
  UpdateInformationBarrierRequest,
} from './informationBarriers';
import {
  ENCRYPTION_NOT_CONFIGURED_MESSAGE,
  SWEEP_POLL_INTERVAL_MS,
  currentExemptions,
  exemptableMembers,
  formatCreatedAt,
  informationBarrierErrorMessage,
  isApplying,
  isEncryptionNotConfigured,
  scopeFor,
  sweepFailed,
  upsertBarrier,
} from './informationBarriers';
import './organisation-information-barriers.scss';

/**
 * Information Barriers: organisation rules that keep a legal case, an
 * investigation or a deal out of the members' assistants. A barrier is plain
 * English; the backend decides per item whether it concerns the barrier, so a
 * person named in one can still come up in unrelated contexts.
 *
 * A barrier applies to everyone minus the people exempted from it; the Team
 * Assistant is always covered. Everything goes through
 * `/organisations/:id/information-barriers`; the list call doubles as the
 * availability check that decides whether the tab exists (2xx shown, 404
 * feature off / 403 not an admin hidden). Before a create or an instruction
 * edit the text is sent to `/check`; a `weak` verdict is shown under the text
 * and the next save goes through regardless. While a barrier's memory sweep
 * is pending or running the card says so and the list is polled; a failed
 * sweep stops the polling and is re-run by switching the barrier off and on.
 */

interface OrganisationInformationBarriersProps {
  organisationId: string;
  members: OrganisationMember[];
  /** Whether the organisation has Team Assistants (they ignore exemptions) */
  teamAssistantsAvailable?: boolean;
  /** Whether the Information Barriers tab is the one showing */
  visible: boolean;
  /**
   * Reports whether the tab may be shown at all: `true` once the list call
   * has answered (any answer but 404/403 — errors are shown in the tab),
   * `false` while loading or when the backend says the feature is off (404)
   * or the caller may not administer it (403).
   */
  onAvailabilityChange: (available: boolean) => void;
}

const EMPTY_FORM: BarrierFormValues = {
  instruction: '',
  exemptIds: [],
};

const EMPTY_INSTRUCTION = 'Say what should stay out.';

const WEAK_RULE_FALLBACK =
  'This may be too vague to apply accurately. Naming the people, references and dates involved helps.';

type FormMode =
  | { mode: 'create' }
  | { mode: 'edit'; barrier: InformationBarrier };

const OrganisationInformationBarriers: FC<
  OrganisationInformationBarriersProps
> = ({
  organisationId,
  members,
  teamAssistantsAvailable = false,
  visible,
  onAvailabilityChange,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const base = `/organisations/${organisationId}/information-barriers`;

  const [barriers, setBarriers] = useState<InformationBarrier[] | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [formMode, setFormMode] = useState<FormMode | null>(null);
  const [form, setForm] = useState<BarrierFormValues>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [invalidInstruction, setInvalidInstruction] = useState(false);
  const [formWarning, setFormWarning] = useState<WeakRuleWarning | null>(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const authHeaders = useCallback(async () => {
    const token = await getAccessTokenSilently();
    return { headers: { Authorization: `Bearer ${token}` } };
  }, [getAccessTokenSilently]);

  const fetchBarriers = useCallback(async () => {
    const { data } = await api.get<ListInformationBarriersResponse>(
      base,
      await authHeaders(),
    );
    return data.barriers;
  }, [base, authHeaders]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const next = await fetchBarriers();
        if (cancelled) return;
        setBarriers(next);
        setNotice(null);
        setLoadError(null);
        onAvailabilityChange(true);
      } catch (err) {
        if (cancelled) return;
        const status = httpStatus(err);
        if (status === 404 || status === 403) {
          onAvailabilityChange(false);
          return;
        }
        if (isEncryptionNotConfigured(err)) {
          setNotice(ENCRYPTION_NOT_CONFIGURED_MESSAGE);
        } else {
          console.error('Error loading information barriers:', err);
          setLoadError('Could not load information barriers.');
        }
        onAvailabilityChange(true);
      }
    })();
    return () => {
      cancelled = true;
      onAvailabilityChange(false);
    };
  }, [fetchBarriers, onAvailabilityChange]);

  const applying = barriers?.some(isApplying) ?? false;
  useEffect(() => {
    if (!applying) return;
    let cancelled = false;
    const timer = setInterval(async () => {
      try {
        const next = await fetchBarriers();
        if (!cancelled) setBarriers(next);
      } catch (err) {
        console.error('Error refreshing information barriers:', err);
      }
    }, SWEEP_POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [applying, fetchBarriers]);

  if (!visible) return null;

  const eligible = exemptableMembers(members);
  const exemptedMembers = (barrier: InformationBarrier) =>
    currentExemptions(barrier.scope, members)
      .map((id) => eligible.find((m) => m.id === id))
      .filter((m): m is OrganisationMember => !!m);

  const openForm = (next: FormMode) => {
    setForm(
      next.mode === 'edit'
        ? {
            instruction: next.barrier.instruction,
            exemptIds: currentExemptions(next.barrier.scope, members),
          }
        : EMPTY_FORM,
    );
    setFormError(null);
    setInvalidInstruction(false);
    setFormWarning(null);
    setFormMode(next);
  };
  const closeForm = () => {
    setFormMode(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setInvalidInstruction(false);
    setFormWarning(null);
  };
  const changeForm = (next: BarrierFormValues) => {
    if (next.instruction !== form.instruction) {
      setFormWarning(null);
      if (invalidInstruction && next.instruction.trim()) {
        setInvalidInstruction(false);
        setFormError(null);
      }
    }
    setForm(next);
  };

  const editingId = formMode?.mode === 'edit' ? formMode.barrier.id : null;

  const save = async (input: CreateInformationBarrierRequest) => {
    setFormError(null);
    setSaving(true);
    try {
      if (editingId) {
        const body: UpdateInformationBarrierRequest = input;
        const { data } = await api.put<InformationBarrierResponse>(
          `${base}/${encodeURIComponent(editingId)}`,
          body,
          await authHeaders(),
        );
        setBarriers((prev) => upsertBarrier(prev ?? [], data.barrier));
      } else {
        const { data } = await api.post<InformationBarrierResponse>(
          base,
          input,
          await authHeaders(),
        );
        setBarriers((prev) => upsertBarrier(prev ?? [], data.barrier));
      }
      closeForm();
    } catch (err) {
      console.error('Error saving information barrier:', err);
      setFormError(
        informationBarrierErrorMessage(err, 'Could not save this barrier.'),
      );
    } finally {
      setSaving(false);
    }
  };

  /** Advisory only: a weak verdict shows a warning, anything else saves. */
  const checkThenSave = async (input: CreateInformationBarrierRequest) => {
    setChecking(true);
    try {
      const body: CheckInformationBarrierRequest = {
        instruction: input.instruction,
      };
      const { data } = await api.post<CheckInformationBarrierResponse>(
        `${base}/check`,
        body,
        await authHeaders(),
      );
      if (data.quality === 'weak') {
        setFormWarning({
          text: data.warning || WEAK_RULE_FALLBACK,
          missing: data.missing ?? [],
        });
        return;
      }
    } catch (err) {
      console.error('Error checking information barrier:', err);
    } finally {
      setChecking(false);
    }
    await save(input);
  };

  const submit = async () => {
    const instruction = form.instruction.trim();
    if (!instruction) {
      setInvalidInstruction(true);
      setFormError(EMPTY_INSTRUCTION);
      return;
    }
    setFormError(null);
    const input: CreateInformationBarrierRequest = {
      instruction,
      scope: scopeFor(form.exemptIds),
    };
    const original = formMode?.mode === 'edit' ? formMode.barrier : undefined;
    const instructionChanged =
      !original || original.instruction !== instruction;
    // A warning already showing means the admin has seen it and saves anyway.
    if (instructionChanged && !formWarning) await checkThenSave(input);
    else await save(input);
  };

  const setEnabled = async (barrier: InformationBarrier, enabled: boolean) => {
    setFeedback(null);
    setBarriers((prev) =>
      (prev ?? []).map((b) => (b.id === barrier.id ? { ...b, enabled } : b)),
    );
    try {
      const body: UpdateInformationBarrierRequest = { enabled };
      const { data } = await api.put<InformationBarrierResponse>(
        `${base}/${encodeURIComponent(barrier.id)}`,
        body,
        await authHeaders(),
      );
      setBarriers((prev) => upsertBarrier(prev ?? [], data.barrier));
    } catch (err) {
      console.error('Error switching information barrier:', err);
      setBarriers((prev) =>
        (prev ?? []).map((b) => (b.id === barrier.id ? barrier : b)),
      );
      setFeedback(
        informationBarrierErrorMessage(
          err,
          `Could not switch this barrier ${enabled ? 'on' : 'off'}.`,
        ),
      );
    }
  };

  const deleting = barriers?.find((b) => b.id === confirmDeleteId) ?? null;
  const closeDelete = () => {
    setConfirmDeleteId(null);
    setDeleteError(null);
  };
  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await api.delete<DeleteInformationBarrierResponse>(
        `${base}/${encodeURIComponent(deleting.id)}`,
        await authHeaders(),
      );
      setBarriers((prev) => (prev ?? []).filter((b) => b.id !== deleting.id));
      closeDelete();
    } catch (err) {
      console.error('Error deleting information barrier:', err);
      setDeleteError(
        informationBarrierErrorMessage(err, 'Could not delete this barrier.'),
      );
    } finally {
      setDeleteBusy(false);
    }
  };

  const loading = barriers === null && !notice && !loadError;
  const canAdd = barriers !== null && !notice;

  return (
    <div
      className='organisation-card organisation-barriers'
      data-testid='organisation-information-barriers'
    >
      <div className='organisation-barriers-header'>
        <h3 className='organisation-card-title'>Information Barriers</h3>
        {canAdd && (
          <button
            type='button'
            className='organisation-barrier-button'
            onClick={() => openForm({ mode: 'create' })}
          >
            Add new barrier
          </button>
        )}
      </div>

      {loading && <p className='organisation-empty'>Loading…</p>}

      {notice && <p className='organisation-notice'>{notice}</p>}

      {loadError && (
        <p className='organisation-feedback error' role='alert'>
          {loadError}
        </p>
      )}

      {feedback && (
        <p className='organisation-feedback error' role='alert'>
          {feedback}
        </p>
      )}

      {barriers?.length === 0 && (
        <div className='organisation-barriers-empty'>
          <ShieldTickIcon aria-hidden='true' />
          <p className='organisation-barriers-empty-title'>
            You’ve not added any Information Barriers yet
          </p>
          <p className='organisation-barriers-empty-text'>
            Choose ‘add new barrier’ above to create your first rule.
          </p>
        </div>
      )}

      {barriers && barriers.length > 0 && (
        <ul className='organisation-barrier-list'>
          {barriers.map((barrier) => {
            const created = formatCreatedAt(barrier.createdAt);
            const title = created ? `Created ${created}` : 'Barrier';
            return (
              <li
                key={barrier.id}
                className={`organisation-barrier${barrier.enabled ? '' : ' off'}`}
                data-testid='information-barrier'
              >
                <div className='organisation-barrier-head'>
                  <h4 className='organisation-barrier-created'>{title}</h4>
                  <div className='organisation-barrier-controls'>
                    <div className='organisation-barrier-controls-group'>
                      <OrganisationToggle
                        on={barrier.enabled}
                        label={`Barrier ${title.toLowerCase()} on`}
                        onChange={(on) => setEnabled(barrier, on)}
                      />
                      <button
                        type='button'
                        className='organisation-barrier-button small'
                        onClick={() => openForm({ mode: 'edit', barrier })}
                      >
                        Edit
                      </button>
                    </div>
                    <span
                      className='organisation-barrier-divider'
                      aria-hidden='true'
                    />
                    <button
                      type='button'
                      className='organisation-barrier-button small delete'
                      onClick={() => {
                        setDeleteError(null);
                        setConfirmDeleteId(barrier.id);
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <div className='organisation-barrier-content'>
                  <p className='organisation-barrier-question'>
                    What should a secretary not read, discuss, or bring up?
                  </p>
                  <p className='organisation-barrier-instruction'>
                    {barrier.instruction}
                  </p>
                </div>
                {barrier.enabled && isApplying(barrier) && (
                  <p className='organisation-barrier-status' role='status'>
                    <span
                      className='organisation-barrier-spinner'
                      aria-hidden='true'
                    />
                    Applying… checking what secretaries already know.
                  </p>
                )}
                {barrier.enabled && sweepFailed(barrier) && (
                  <p className='organisation-barrier-status failed'>
                    <AlertTriangleIcon aria-hidden='true' />
                    This barrier couldn’t finish applying to what secretaries
                    already know. Switch it off and on again to retry.
                  </p>
                )}
                <ScopeSummary
                  exempt={exemptedMembers(barrier)}
                  teamAssistantsAvailable={teamAssistantsAvailable}
                />
              </li>
            );
          })}
        </ul>
      )}

      {formMode && (
        <InformationBarrierModal
          mode={formMode.mode}
          values={form}
          members={eligible}
          teamAssistantsAvailable={teamAssistantsAvailable}
          error={formError}
          invalidInstruction={invalidInstruction}
          warning={formWarning}
          checking={checking}
          saving={saving}
          onChange={changeForm}
          onSubmit={submit}
          onClose={closeForm}
        />
      )}

      {deleting && (
        <HandbookConfirmModal
          title='Delete barrier'
          className='organisation-barrier-delete'
          heading='Delete this barrier?'
          confirmLabel='Delete'
          busyLabel='Deleting…'
          busy={deleteBusy}
          error={deleteError}
          onClose={closeDelete}
          onConfirm={confirmDelete}
        >
          <p>
            Secretaries will be able to read, discuss and bring this up again
            straight away.
          </p>
        </HandbookConfirmModal>
      )}
    </div>
  );
};

export default OrganisationInformationBarriers;
